import Tooltip from '@mui/material/Tooltip';
import { useGetUserOnlineByRegion } from '@practium/features/api';
import { useToast } from '@practium/ui';
import React, { useEffect, useMemo } from 'react';
import { useBrazilMap } from '../../hooks/useBrazilMap';
import { getUserMarkers, getLightColor, getLightGradientId } from '../../utils/mapUtils';
import styles from './BrazilMap.module.css';

const BrazilMapSkeleton = () => (
	<div className={styles.skeleton}>
		<div className={styles.skeletonCircle}>
			<div className={styles.skeletonRegions}>
				<div className={`${styles.skeletonRegion} ${styles.skeletonR1}`} />
				<div className={`${styles.skeletonRegion} ${styles.skeletonR2}`} />
				<div className={`${styles.skeletonRegion} ${styles.skeletonR3}`} />
				<div className={`${styles.skeletonRegion} ${styles.skeletonR4}`} />
				<div className={`${styles.skeletonRegion} ${styles.skeletonR5}`} />
				<div className={`${styles.skeletonRegion} ${styles.skeletonR6}`} />
			</div>
		</div>
	</div>
);

const BrazilMap: React.FC = () => {
	const toast = useToast();
	const { brazilGeo, isLoading, error, pathGenerator, projection } = useBrazilMap();
	const { data, isPending: isUsersLoading, isError: isUsersError } = useGetUserOnlineByRegion();

	const markersByState = useMemo(() => {
		return new Map(
			data?.states.map((state) => {
				const feature = brazilGeo?.features.find((item) => item.properties.sigla === state.state);
				return [
					state.state,
					feature
						? getUserMarkers(feature, state.onlineUsers, pathGenerator, projection)
						: { positions: [], size: 0 },
				];
			})
		);
	}, [brazilGeo, data, pathGenerator, projection]);

	useEffect(() => {
		if (error) {
			toast.error(error);
		}
	}, [error]);

	if (error || isUsersError) {
		return (
			<div className={styles.errorContainer}>
				<p>
					{error
						? 'Não foi possível carregar o mapa'
						: 'Não foi possível carregar os usuários online'}
				</p>
			</div>
		);
	}

	if (isLoading || !brazilGeo || isUsersLoading) {
		return <BrazilMapSkeleton />;
	}

	const viewBox = '0 0 800 800';

	return (
		<div className={styles.brazilMapContainer}>
			<svg viewBox={viewBox} className={styles.brazilMapSvg}>
				<defs>
					<linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
						<stop offset="0%" stopColor="#689ee6" />
						<stop offset="50%" stopColor="#8B9FE8" />
						<stop offset="100%" stopColor="#A89FEA" />
					</linearGradient>

					<filter id="lightGlow">
						<feGaussianBlur stdDeviation="3" result="coloredBlur" />
						<feMerge>
							<feMergeNode in="coloredBlur" />
							<feMergeNode in="coloredBlur" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>

					<radialGradient id="lightGradientPink1">
						<stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
						<stop offset="20%" stopColor="#cf76e5" stopOpacity="0.95" />
						<stop offset="50%" stopColor="#cf76e5" stopOpacity="0.85" />
						<stop offset="100%" stopColor="rgba(207, 118, 229, 0.4)" />
					</radialGradient>

					<radialGradient id="lightGradientPink2">
						<stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
						<stop offset="20%" stopColor="#e54edb" stopOpacity="0.95" />
						<stop offset="50%" stopColor="#e54edb" stopOpacity="0.85" />
						<stop offset="100%" stopColor="rgba(229, 78, 219, 0.4)" />
					</radialGradient>
				</defs>

				<g>
					{brazilGeo.features.map((feature) => {
						const properties = feature.properties;
						const stateId = properties?.id || '';
						const stateSigla = properties?.sigla || '';
						const stateName = properties?.name || '';

						return (
							<path
								className={styles.stateOutline}
								key={`state-${stateId}-${stateSigla}-${stateName}`}
								d={pathGenerator(feature) || ''}
								fill="url(#mapGradient)"
								stroke="#FFFFFF"
								strokeWidth={0.3}
								opacity={0.95}
							/>
						);
					})}
				</g>

				{data?.states.map((state) => {
					if (state.onlineUsers <= 0) return null;

					const featureIndex = brazilGeo.features.findIndex(
						(feature) => feature.properties.sigla === state.state
					);
					if (featureIndex === -1) return null;

					const { positions, size } = markersByState.get(state.state)!;
					const lightColor = getLightColor(featureIndex);
					const gradientId = getLightGradientId(featureIndex);

					return positions.map(([x, y], markerIndex) => (
						<Tooltip
							key={`online-${state.state}-${markerIndex}`}
							arrow
							placement="top-start"
							enterDelay={0}
							classes={{ tooltip: styles.mapTooltip, arrow: styles.tooltipArrow }}
							title={
								<span className={styles.tooltipContent}>
									<strong>{state.name}</strong>
									<span aria-hidden="true">·</span>
									<span>
										{state.onlineUsers} {state.onlineUsers === 1 ? 'usuário' : 'usuários'}
									</span>
								</span>
							}
						>
							<g
								className={styles.marker}
								tabIndex={0}
								role="img"
								aria-label={`${state.name}: ${state.onlineUsers} ${state.onlineUsers === 1 ? 'usuário online' : 'usuários online'}`}
							>
								<circle
									className={styles.markerTarget}
									cx={x}
									cy={y}
									r={Math.max(size * 4.5, 16)}
									fill="transparent"
								/>
								<circle cx={x} cy={y} r={size * 3.5} fill={lightColor} opacity={0.25}>
									<animate
										attributeName="r"
										values={`${size * 2.5};${size * 4.5};${size * 2.5}`}
										dur="3s"
										repeatCount="indefinite"
									/>
									<animate
										attributeName="opacity"
										values="0.25;0.1;0.25"
										dur="3s"
										repeatCount="indefinite"
									/>
								</circle>

								<circle cx={x} cy={y} r={size * 2} fill={lightColor} opacity={0.4}>
									<animate
										attributeName="r"
										values={`${size * 1.5};${size * 2.5};${size * 1.5}`}
										dur="2s"
										repeatCount="indefinite"
									/>
									<animate
										attributeName="opacity"
										values="0.4;0.2;0.4"
										dur="2s"
										repeatCount="indefinite"
									/>
								</circle>

								<circle
									cx={x}
									cy={y}
									r={size * 1.2}
									fill={`url(#${gradientId})`}
									filter="url(#lightGlow)"
									opacity={0.95}
								/>

								<circle cx={x} cy={y} r={size * 0.5} fill="#FFFFFF" opacity={1}>
									<animate
										attributeName="opacity"
										values="1;0.8;1"
										dur="1.5s"
										repeatCount="indefinite"
									/>
								</circle>
							</g>
						</Tooltip>
					));
				})}
			</svg>
		</div>
	);
};

export default BrazilMap;
